import { Platform, PermissionsAndroid } from 'react-native';
import { request, requestNotifications, check } from 'react-native-permissions';

import { invert as _invert, forIn as _forIn } from 'lodash';

import {
  permissions,
  IOSPermissionTypes,
  localPermissions as localPermissionTypes,
  androidPermissionTypes,
} from './permissionTypes';

export default class PermissionsManager {
  _getResults(results, ...args) {
    const types = _invert(permissions);
    const data = {};
    _forIn(results, (value, key) => {
      if (key === types.PUSH_NOTIFICATIONS) {
        data[types[key]] = value.status === PermissionsAndroid.RESULTS.GRANTED;
      } else {
        data[types[key]] = value === PermissionsAndroid.RESULTS.GRANTED;
      }
    });
    return Object.assign(data, ...args);
  }

  _requestIOS = async function (requestedPermissions) {
    const permissionResults = {};
    for (const permission of requestedPermissions) {
      let result;
      if (permission === IOSPermissionTypes.PUSH_NOTIFICATIONS) {
        result = await requestNotifications(['alert', 'badge', 'sound']);
      } else {
        result = await request(permission);
      }
      permissionResults[permission] = result;
    }
    return permissionResults;
  };

  _request(data = []) {
    if (Platform.OS === 'android') {
      return PermissionsAndroid.requestMultiple(data);
    } else {
      return this._requestIOS(data);
    }
  }

  _getPermissionHandler = (requestedPermissions, permission, customPermissionsHandling) => {
    if (requestedPermissions[permission]) {
      return customPermissionsHandling.grantPermission;
    }
    return customPermissionsHandling.declinePermission;
  };

  _getRequestPermissionsData(config, requestedPermissions, customPermissionsHandling) {
    const data = [];
    const local = {};
    const reject = {};
    const localPermissions = [];
    let permissionHandler;

    for (const permission in requestedPermissions) {
      if (!(permission in permissions)) {
        throw new Error(`Permission ${permission} is not supported yet`);
      }
      switch (permission) {
        case localPermissionTypes.NETWORK:
        case localPermissionTypes.PERSONALIZED_SERVICES:
        case localPermissionTypes.BIOMETRIC:
          permissionHandler = this._getPermissionHandler(
            requestedPermissions,
            permission,
            customPermissionsHandling,
          );
          localPermissions.push({ permission, config, permissionHandler });
          local[permission] = requestedPermissions[permission];
          break;

        case androidPermissionTypes.READ_CALL_LOG:
        case androidPermissionTypes.WRITE_CALL_LOG:
          if (Platform.Version >= 28 && requestedPermissions[permission]) {
            data.push(permissions[permission]);
          } else {
            reject[permission] = false;
          }
          break;

        default:
          if (requestedPermissions[permission]) {
            data.push(permissions[permission]);
          } else {
            reject[permission] = false;
          }
          break;
      }
    }
    return { data, localPermissions, local, reject };
  }

  async requestPermissions(
    config = {},
    requestedPermissions = {},
    customPermissionsHandling = async () => {},
  ) {
    const keys = Object.keys(requestedPermissions);
    if (keys.length == 0) {
      return;
    }

    const { data, localPermissions, local, reject } = this._getRequestPermissionsData(
      config,
      requestedPermissions,
      customPermissionsHandling,
    );
    const results = await this._request(data);
    this.requestLocalPermission(localPermissions);
    return this._getResults(results, local, reject);
  }

  requestLocalPermission = (localPermissions = []) => {
    localPermissions.forEach(async permissionObject => {
      const { permission, config, permissionHandler } = permissionObject;
      try {
        await permissionHandler(permission, config);
      } catch (err) {
        console.log(err);
      }
    });
  };

  static getPermissionStatus(permission) {
    return check(permission);
  }
}
